import React from 'react';
import './AllocationControl.scss';
import { useTranslation } from 'react-i18next';
import { Slider } from 'primereact/slider';
import { InputNumber } from 'primereact/inputnumber';

interface AllocationControlProps {
  /** null = no limit: the input is empty (placeholder) and the slider rests at the floor. */
  value: number | null;
  /** The floor: the prompts in use (edit) or the selected prompts (wizard). */
  min: number;
  /** The ceiling: the floor plus what the pool still has for this project. */
  max: number;
  /** Prompts the pool still has after `value` — shown under the slider. */
  free: number;
  /** null = the input was cleared, i.e. the project has no limit. */
  onChange: (value: number | null) => void;
  disabled?: boolean;
}

/**
 * A project's prompt allocation: the hub dialog's row control (slider + number input) with the two
 * numbers that bound it — "min. N used" and what the pool still has. Clamps to [min, max]; clearing
 * the input removes the limit (null).
 */
const AllocationControl: React.FC<AllocationControlProps> = ({
  value,
  min,
  max,
  free,
  onChange,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const clamp = (next: number) => onChange(Math.max(min, Math.min(max, next)));

  return (
    <div className="allocation-control">
      <div className="allocation-control__row">
        <Slider
          className="allocation-control__slider"
          value={value ?? min}
          min={min}
          max={max}
          step={1}
          disabled={disabled || max <= min}
          onChange={(e) => clamp(e.value as number)}
        />
        <InputNumber
          className="allocation-control__input"
          value={value}
          min={min}
          max={max}
          useGrouping={false}
          placeholder={t('promptAllocationNoLimit')}
          disabled={disabled}
          onValueChange={(e) => (e.value === null || e.value === undefined ? onChange(null) : clamp(e.value))}
        />
      </div>
      <div className="allocation-control__meta">
        <span>{t('minUsed', { count: min })}</span>
        <span>{t('promptAllocationFree', { count: free })}</span>
      </div>
    </div>
  );
};

export default AllocationControl;
